/* global process */
import { action, observable, computed } from 'mobx'
import { computedFn } from 'mobx-utils'
import Http, { getHttpClient } from '../services/http'
import { dataSources } from '../utils/data-sources'
import { emit } from '../utils/emit'

const isServer = typeof window === 'undefined'

const constantItems = {
  btpSession: 'btp:session',
  btpTenant: 'btp:tenant',
  btpHmrcAuth: 'btp:hmrc:auth',
  btpSysAdmin: 'btp:sysAdmin',
  btpSysAdminUser: 'btp:sysAdminUser',
  btpSysAdminTenant: 'btp:sysAdminTenant'
}

export class SessionStore {
  @observable tenant = null
  @observable session = null
  @observable user = null
  @observable hmrcAuth = null
  @observable lastError = null
  @observable isLoading = false
  @observable sysAdmin = null
  @observable sysAdminUser = null
  @observable sysAdminTenant = null
  @observable sysAdminLoggedin = false
  @observable sessionMessageVisible = false

  constructor () {
    this.ds = dataSources()

    if (!isServer) {
      if (typeof window !== 'undefined' && window.sessionStorage.getItem(constantItems.btpSession)) {
        this.session = JSON.parse(window.sessionStorage.getItem(constantItems.btpSession))
      }
      if (typeof window !== 'undefined' && window.sessionStorage.getItem(constantItems.btpTenant)) {
        this.tenant = JSON.parse(window.sessionStorage.getItem(constantItems.btpTenant))
      }
      if (typeof window !== 'undefined' && window.sessionStorage.getItem(constantItems.btpHmrcAuth)) {
        this.hmrcAuth = JSON.parse(window.sessionStorage.getItem(constantItems.btpHmrcAuth))
      }
      if (typeof window !== 'undefined' && window.sessionStorage.getItem('btp:user')) {
        this.user = JSON.parse(window.sessionStorage.getItem('btp:user'))
      }

      if (typeof window !== 'undefined' && window.sessionStorage.getItem(constantItems.btpSysAdmin)) {
        this.sysAdmin = JSON.parse(window.sessionStorage.getItem(constantItems.btpSysAdmin))
      }
      if (typeof window !== 'undefined' && window.sessionStorage.getItem(constantItems.btpSysAdminUser)) {
        this.sysAdminUser = JSON.parse(window.sessionStorage.getItem(constantItems.btpSysAdminUser))
      }
      if (typeof window !== 'undefined' && window.sessionStorage.getItem(constantItems.btpSysAdminTenant)) {
        this.sysAdminTenant = JSON.parse(window.sessionStorage.getItem(constantItems.btpSysAdminTenant))
      }
      if (typeof window !== 'undefined' && window.sessionStorage.getItem('btp:sysAdminLoggedin')) {
        this.sysAdminLoggedin = JSON.parse(window.sessionStorage.getItem('btp:sysAdminLoggedin'))
      }
    }
  }

  @action setTenant = (n) => {
    this.tenant = n
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(constantItems.btpTenant, JSON.stringify(n))
    }
  }

  @action setSysAdmin = (sa) => {
    this.sysAdmin = sa
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(constantItems.btpSysAdmin, JSON.stringify(sa))
    }
  }

  @action setSysAdminUser = (sa) => {
    this.sysAdminUser = sa
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(constantItems.btpSysAdminUser, JSON.stringify(sa))
    }
  }

  @action removeSysAdminUser = () => {
    this.sysAdminUser = null
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(constantItems.btpSysAdminUser)
    }
  }

  @action setSysAdminTenant = (sa) => {
    this.sysAdminTenant = sa
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(constantItems.btpSysAdminTenant, JSON.stringify(sa))
    }
  }

  @action removeSysAdminTenant = () => {
    this.sysAdminTenant = null
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(constantItems.btpSysAdminTenant)
    }
  }

  @action setSession = (s) => {
    this.session = s
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(constantItems.btpSession, JSON.stringify(s))
    }
  }

  @action setHmrcAuth = (h) => {
    this.hmrcAuth = h
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(constantItems.btpHmrcAuth, JSON.stringify(h))
    }
  }

  @action removeHmrcAuth = (env) => {
    this.hmrcAuth = null

    const http = Http.getInstance(this)
    return http.deleteData(`${this.ds.hmrcAuthenticationStatus}/${env.HMRC_APP_ID}/${this.tenant}`, {
      headers: {
        'x-api-key': env.TESS_API_KEY
      }
    })
      .then((res) => {
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem(constantItems.btpHmrcAuth)
        }
      })
  }

  @action registerHmrcCode = (code, env) => {
    const http = Http.getInstance(this)
    const postData = {
      appId: env.HMRC_APP_ID,
      taxId: this.tenant,
      code
    }
    return http.postData(this.ds.hmrcAuthenticationCode, postData, {
      headers: {
        'x-api-key': env.TESS_API_KEY
      }
    })
      .then(() => {
        this.hmrcAuth = this.tenant
        if (typeof window !== 'undefined') {
          window.sessionStorage.setItem(constantItems.btpHmrcAuth, JSON.stringify(this.tenant))
        }
      })
  }

  @action setUser = (u) => {
    this.user = u
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('btp:user', JSON.stringify(u))
    }
  }

  @action setLastError = (e) => {
    this.lastError = e
  }

  @action destroySessionWithMessage = (message) => {
    const overlay = document.querySelector('#overlayBG')

    if (overlay) {
      this.sessionMessageVisible = true

      overlay.classList.remove('d-none')
      document.querySelector('#overlayBGText').innerHTML = message
      overlay.classList.add('d-block')

      this.destroy()
    }
  }

  @action destroy = () => {
    const http = Http.getInstance(this)
    const postData = {
      'refresh_token': (this.session) ? this.session.refresh_token : null
    }
    const destroyPromise = (this.session) ? http.postData(this.ds.userLogout, postData) : Promise.resolve()

    return destroyPromise
      .then((res) => {
        this.destroySession()
      })
      .catch(() => {
        this.destroySession()
      })
  }

  @action destroySession = () => {
    this.session = null
    this.hmrcAuth = null
    this.user = null
    this.sysAdmin = null
    this.sysAdminUser = null
    this.sysAdminTenant = null
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(constantItems.btpSession)
      window.sessionStorage.removeItem(constantItems.btpHmrcAuth)
      window.sessionStorage.removeItem('btp:user')
      window.sessionStorage.removeItem(constantItems.btpSysAdmin)
      window.sessionStorage.removeItem(constantItems.btpSysAdminUser)
      window.sessionStorage.removeItem(constantItems.btpSysAdminTenant)
    }
  }

  @computed get isLoggedIn () {
    return this.session != null && this.user != null && this.tenant != null
  }

  @computed get isAdminLoggedIn () {
    return this.session != null
  }

  @computed get hasTenant () {
    return this.tenant != null
  }

  @computed get hasHmrcAuth () {
    return this.hmrcAuth != null
  }

  @computed get isBaseEdition () {
    if (this.user) {
      return this.user.tenant.Subscription.edition === 'basic'
    }
  }

  @computed get isFullEdition () {
    if (this.user) {
      return this.user.tenant.Subscription.edition === 'full'
    }
  }

  @computed get isAgentServiceAccount () {
    let serviceAccount = false
    if (this.user) {
      const subScription = this.user.tenant.Subscription.Options.slice()
      subScription.forEach((role) => {
        if (role.name === 'AgentServiceAccount') {
          serviceAccount = role.value === 'yes'
        }
      })
    }
    return serviceAccount
  }

  @computed get consultantModeCheck () {
    let consultantMode = false
    if (this.user) {
      this.user.tenant.Subscription.Options.forEach((option) => {
        if (option.name === 'ConsultantMode') {
          consultantMode = option.value === 'yes'
        }
      })
    }
    return consultantMode
  }

  @computed get isAdmin () {
    let isAdmin = false
    if (this.user) {
      const userRoles = this.user.roles.slice()
      userRoles.forEach((role) => {
        if (role === 'Administrator') {
          isAdmin = true
        }
      })
    }
    return isAdmin
  }

  @computed get isSysAdminAdministrator () {
    let isSysAdminAdministrator = false
    if (this.sysAdminUser) {
      const realmRoles = this.sysAdminUser.realmRoles.slice()
      realmRoles.forEach((role) => {
        if (role === 'btp-admin-administrator') {
          isSysAdminAdministrator = true
        }
      })
    }
    return isSysAdminAdministrator
  }

  @computed get isStandard () {
    let isStandard = false
    if (this.user) {
      const userRoles = this.user.roles.slice()
      userRoles.forEach((role) => {
        if (role === 'Standard') {
          isStandard = true
        }
      })
    }
    return isStandard
  }

  @computed get isApprover () {
    let isApprover = false
    if (this.user) {
      const userRoles = this.user.roles.slice()
      userRoles.forEach((role) => {
        if (role === 'Approver') {
          isApprover = true
        }
      })
    }
    return isApprover
  }

  @action login = (username, password, env) => {
    if (this.sysAdmin) {
      const http = Http.getInstance(this)
      const postData = {
        username,
        password
      }
      return http.postData(this.ds.adminLogin, postData)
        .then((res) => {
          if (res.access_token) {
            this.setSession(res)
            return http.getData(this.ds.currentUser)
          } else {
            throw new Error(JSON.stringify(res))
          }
        })
        .then((response) => {
          this.setSysAdminUser(response.data)
        })
        .catch(e => {
          if (e.response) {
            return e.response
          } else {
            return e
          }
        })
    } else {
      const http = Http.getInstance(this)
      const realm = this.tenant
      const postData = {
        realm,
        username,
        password
      }
      return http.postData(this.ds.userAuthLogin, postData)
        .then((res) => {
          if (res.access_token) {
            this.setSession(res)
            return http.getData(this.ds.currentUser)
          } else {
            throw new Error(JSON.stringify(res))
          }
        })
        .then((user) => {
          this.setUser(user.data)
          return http.getData(`${this.ds.hmrcAuthenticationStatus}/${env.HMRC_APP_ID}/${realm}`, {
            headers: {
              'x-api-key': env.TESS_API_KEY
            }
          })
        })
        .then((res) => {
          if (res.result === 'OK') {
            this.setHmrcAuth(realm)
          }
        })
    }
  }

  @action tenantExists (name) {
    const inst = getHttpClient()
    return inst
      .get(`${this.ds.tenantExists}/${name}`)
      .then((response) => {
        return response.data
      })
  }

  @action register = (data) => {
    const inst = getHttpClient()
    return inst
      .post(this.ds.tenantRegister, data)
      .then((response) => {
        return response.data
      }).catch(e => {
        if (e.response) {
          return e.response
        } else {
          return e
        }
      })
  }

  @action registerStatus = (registrationId) => {
    const inst = getHttpClient()
    return inst
      .get(`${this.ds.tenantRegisterStatus}/${registrationId}`)
      .then((response) => {
        return response.data
      }).catch(e => {
        if (e.response) {
          return e.response
        } else {
          return e
        }
      })
  }

  @action forgotPassword = (email) => {
    const http = Http.getInstance(this)
    const realm = this.tenant
    const postData = {
      realm,
      email
    }
    return http.postData(this.ds.userForgotPassword, postData)
      .then((res) => {
        if (res.status === 'success') {
          return res
        } else {
          return res.data
        }
      }).catch((e) => {
        return e.data
      })
  }

  @action changePassword = (currentpassword, newpassword, confirmpassword) => {
    const http = Http.getInstance(this)
    const postData = {
      currentpassword,
      newpassword,
      confirmpassword
    }
    const url = '/api/v1/keycloak/user/password'
    return http.putData(url, postData)
      .then((res) => {
        if (res.status === 'success') {
          return res
        } else {
          return res.data
        }
      }).catch((e) => {
        return e.data
      })
  }

  @action renameTenant = (data, tenantId) => {
    const inst = getHttpClient()
    return inst
      .put(`/tenant/${tenantId}/rename`, data)
      .then((response) => {
        return response.data
      }).catch(e => {
        if (e.response) {
          return e.response
        } else {
          return e
        }
      })
  }
}